import React, { useEffect, useState } from 'react';
import { DateField } from '../DateField';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, ScanSearch, Plus, Trash2 } from 'lucide-react';
import { runAiJob } from '../../lib/aiJob';
import { DeadlineRow } from './DeadlineRow';
import { BriefDialog } from './BriefDialog';
import { RegionSelect } from './RegionSelect';

export const DeadlinesDialog = ({ matter, open, onOpenChange, onUpdated }) => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [procs, setProcs] = useState([]);
  const [note, setNote] = useState('');
  const [regionInfo, setRegionInfo] = useState(null);
  const [proc, setProc] = useState('');
  const [start, setStart] = useState('');
  const [busy, setBusy] = useState('');
  const [drafting, setDrafting] = useState(null);

  const load = async () => {
    const r = await api.get('/crm/deadlines');
    setItems(r.data.deadlines.filter((d) => d.matter_id === matter.id));
  };
  useEffect(() => {
    load();
    api.get(`/crm/deadlines/procedures?country=${matter.country || 'chile'}&matter_id=${matter.id}`).then((r) => { setProcs(r.data.procedures); setNote(r.data.note || ''); setRegionInfo(r.data); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const detect = async () => {
    setBusy('detect');
    try {
      const r = await runAiJob(api, `/crm/matters/${matter.id}/deadlines/detect`, undefined, { timeout: 240000 });
      toast.success(r.added.length ? `${r.added.length} plazo(s) agregados` : 'No se encontraron plazos nuevos con fecha');
      load(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'No se pudo analizar'); } finally { setBusy(''); }
  };
  const addLegal = async () => {
    if (!proc || !start) return toast.error('Elige el trámite y la fecha de notificación');
    setBusy('legal');
    try {
      await api.post(`/crm/matters/${matter.id}/deadlines/legal`, { procedure: proc, start_date: start });
      toast.success('Plazo legal agregado'); load(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo calcular'); } finally { setBusy(''); }
  };
  const setNotification = async (d, date) => {
    try { await api.patch(`/crm/matters/${matter.id}/deadlines/${d.id}`, { start_date: date }); toast.success('Vencimiento calculado'); load(); onUpdated(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo calcular'); }
  };
  const remove = async (id) => {
    try { await api.delete(`/crm/matters/${matter.id}/deadlines/${id}`); toast.success('Plazo eliminado'); load(); onUpdated(); }
    catch { toast.error('No se pudo eliminar'); }
  };
  const sel = procs.find((p) => p.type === proc);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deadlines-dialog" className="bg-slate-950 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-white">Plazos de {matter.title}</DialogTitle>
          <DialogDescription className="text-slate-400">Te avisamos en la campana 3 días antes, 1 día antes y el día del vencimiento.</DialogDescription>
        </DialogHeader>
        <button data-testid="deadlines-detect-btn" onClick={detect} disabled={!!busy} className="w-full border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 rounded-lg py-2 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          {busy === 'detect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}{busy === 'detect' ? 'Leyendo documentos (≈1 min)...' : 'Detectar plazos en documentos importados'}
        </button>
        {procs.length === 0 ? (
          <p data-testid="deadlines-legal-unavailable" className="text-xs text-slate-400 bg-slate-900 border border-slate-700 rounded-xl p-3">El cálculo automático según la ley aún no está disponible para este país. Detecta los plazos desde los documentos importados del caso.</p>
        ) : (
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-3 space-y-2">
          <p className="text-xs uppercase tracking-wider text-amber-400 font-semibold">Calcular según la ley</p>
          <RegionSelect matter={matter} info={regionInfo} onChange={(v) => setRegionInfo((x) => ({ ...x, region: v }))} />
          <div className="grid sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-2">
            <select data-testid="deadlines-procedure-select" value={proc} onChange={(e) => setProc(e.target.value)} className="w-full min-w-0 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm">
              <option value="">Trámite o recurso…</option>
              {procs.map((p) => <option key={p.type} value={p.type}>{p.name} · {p.days} días {{ habiles: 'hábiles', corridos: 'corridos', corridos_prorroga: 'corridos' }[p.day_type] || p.day_type}</option>)}
            </select>
            <DateField testid="deadlines-start-date" value={start} onChange={setStart} className="py-2" />
            <button data-testid="deadlines-add-legal-btn" onClick={addLegal} disabled={!!busy} className="bg-amber-500 text-slate-950 font-semibold rounded-lg px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-50"><Plus className="w-4 h-4" />Agregar</button>
          </div>
          {sel && <p className="text-xs text-slate-400">{sel.legal_basis} · se cuenta desde {sel.start_event}</p>}
          {note && <p data-testid="deadlines-country-note" className="text-[11px] text-amber-200/80">{note}</p>}
        </div>
        )}
        <div data-testid="deadlines-list" className="space-y-2">
          {items.length === 0 && <p className="text-sm text-slate-500">Sin plazos registrados.</p>}
          {items.map((d) => (
            <div key={d.id} className="flex items-start gap-2">
              <div className="flex-1 min-w-0"><DeadlineRow d={d} onSetNotification={setNotification} onDraft={setDrafting} /></div>
              <button data-testid={`deadline-delete-${d.id}`} onClick={() => remove(d.id)} aria-label="Eliminar plazo" className="mt-3 text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {drafting && <BriefDialog matter={matter} deadline={drafting} open={!!drafting} onOpenChange={(o) => !o && setDrafting(null)} onUpdated={onUpdated} />}
      </DialogContent>
    </Dialog>
  );
};
